import {
  ModalBackdrop,
  ModalContent,
  Modal as ModalUI,
} from "@repo/components/src/ui/modal";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { useModal } from "./hook";

const ModalRoot = forwardRef(function ModalRoot(_, ref) {
  const {
    show,
    hide,
    isVisible,
    getIsVisible,
    options,
    enableSwipeToDismiss,
    data,
    contentProps,
  } = useModal();
  const translateY = useSharedValue(0);
  const translateX = useSharedValue(0);

  // This must use useCallback to ensure the ref doesn't
  // get set to null and then a new ref every render.
  useImperativeHandle(
    ref,
    useCallback(
      () => ({
        show,
        hide,
        isVisible: getIsVisible,
      }),
      [hide, show, getIsVisible],
    ),
  );
  const gesture = Gesture.Pan()
    .enabled(enableSwipeToDismiss ?? false)
    .onUpdate((event) => {
      // Only allow swiping down
      if (event.translationY > 0) {
        translateY.value = event.translationY;
        translateX.value = event.translationX;
      }
    })
    .onEnd((event) => {
      if (event.translationY > 150 || event.velocityY > 500) {
        // Trigger close if swiped far enough or fast enough
        scheduleOnRN(hide);
      }
      translateY.value = withSpring(0);
      translateX.value = withSpring(0);
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: translateY.value },
      { translateX: translateX.value },
    ],
  }));
  return (
    <ModalUI isOpen={isVisible} onClose={hide} {...options}>
      <ModalBackdrop />
      {!!enableSwipeToDismiss ? (
        <GestureDetector gesture={gesture}>
          <Animated.View
            style={animatedStyle}
            className={"w-full h-full items-center justify-center"}
          >
            <ModalContent {...contentProps}>{data.children}</ModalContent>
          </Animated.View>
        </GestureDetector>
      ) : (
        <ModalContent {...contentProps}>{data.children}</ModalContent>
      )}
    </ModalUI>
  );
});

export default ModalRoot;
